import {
    NavigationState, Route,
} from './types'
import {
    createSlice,
    isAsyncThunkAction,
    isFulfilled,
} from '@reduxjs/toolkit'
import {
    isChannelSuspended,
    isDescribed,
    isScheduledAppointment,
} from 'utils/channels'
import {
    setSlice,
    sliceFilters,
} from '../store/storage'

import {RootState} from 'store'

import {describeChannel} from 'api/action/describeChannel'
import {reducers as extraReducers} from './reducers'
import ninchatClient from '../api/NinchatClient'
import router from 'navigation/router'
import {startAppListening} from '../middlewares/listener'
import {updateMember} from 'api/action/updateMember'
import {updateRealm} from '../state/ui'

export const initialState: NavigationState = {
    position: -1,
    routes: [],
}

const navigationSlice = createSlice({
    extraReducers,
    initialState,
    name: 'NAVIGATION',
    reducers: {},
})

sliceFilters.set('navigation', {routes: true})

startAppListening({
    effect: (_action, listenerApi) => {
        const {
            position,
            routes,
        } = listenerApi.getState().navigation
        setSlice({navigation: {routes: [routes[position]]} as NavigationState})
    },
    predicate: (action) => typeof action === 'object' && action.type.startsWith('NAVIGATION/'),
})

startAppListening({
    effect: async (_action, listenerApi) => {
        const {
            position,
            routes,
        } = listenerApi.getState().navigation

        const route = routes[position]

        if (!router.isRoute<'Channel'>('Channel', route)) {
            return
        }

        const {channel_id} = route.params

        let channel = listenerApi.getState().ninchat.user_channels[channel_id]

        if (channel && !isScheduledAppointment(channel)) {
            return
        }

        if (!isDescribed(channel)) {
            try {
                channel = await listenerApi.dispatch(describeChannel({channel_id})).unwrap()
            } catch {} // eslint-disable-line no-empty
        }

        if (!isScheduledAppointment(channel)) {
            return
        }

        const {user_id} = listenerApi.getState().ninchat

        if (!user_id) {
            return
        }

        const invitee = channel.channel_members?.[user_id]?.member_attrs?.invitee

        if (!isChannelSuspended(channel) && invitee) {
            listenerApi.dispatch(updateMember({
                channel_id,
                member_attrs: {invitee: false},
                user_id,
            }))
        }
    },
    predicate: (action, currentState) => {
        const {type} = action

        const {
            position,
            routes,
        } = currentState.navigation

        const test = [
            ninchatClient.connected,
            type.startsWith(`${navigationSlice.name}/`),
            isAsyncThunkAction(action) ? isFulfilled(action) : true,
            router.isRoute<'Channel'>('Channel', routes[position]),
        ]

        return test.every((e) => e)
    },
})

export const getRealmId = (state: RootState, route: Route) => {
    if (router.isRoute('Accept', route)) {
        return state.ninchat.user_queues?.[route.params.queue_id]?.realm_id
    } else if (router.isRoute('Channel', route) || router.isRoute('ChannelSettings', route)) {
        return state.ninchat.user_channels?.[route.params.channel_id]?.realm_id
    } else if (router.isRoute('Queue', route) || router.isRoute('QueueSettings', route) || router.isRoute('QueueStatistics', route)) {
        return state.ninchat.user_queues?.[route.params.queue_id]?.realm_id
    } else if (router.isRoute('Realm', route)) {
        return route.params.realm_id
    } else if (router.isRoute('RegisteredAudience', route) || router.isRoute('Transcript', route)) {
        return Object.values(state.ninchat.user_channels ?? {}).find(
            (channel) => channel.channel_attrs?.audience_id === route.params.audience_id,
        )?.realm_id
    }
    return undefined
}

startAppListening({
    effect: (_action, listenerApi) => {
        const {
            position, routes,
        } = listenerApi.getState().navigation
        const route = routes[position]
        if (!route) {
            return
        }
        const state = listenerApi.getState()
        const currentRealmId = state.ui?.realm_id

        const realmId = getRealmId(state, route)
        if (realmId && realmId !== currentRealmId) {
            listenerApi.dispatch(updateRealm(realmId))
        }
    },
    predicate: (action): boolean => action.type.startsWith('NAVIGATION/'),
})


export const {reducer: navigationReducer} = navigationSlice
